import {z as zod} from "zod";
import {
    BackendFilterOperations,
    BackendFilterTypes,
    BackendFilterValueTypes,
    CanvasControlType,
    ColumnSortType,
    ComposedChartType,
    DateRangeOptions,
    EratoDataTypes,
    FilterOperations,
    Granularity,
    NumberSystems,
    TableColumnDataTypes,
    TableColumnTextOverflow,
    WidgetType,
    WidgetValueType,
    type AtomicFilter,
    type BackendFilter,
    type CanvasControl,
    type CanvasControls,
    type ChartSeriesDetails,
    type ComboChartSeriesDetails,
    type GranularityForWidget,
    type ReportPage,
    type StoredWidget,
    type WidgetAttributeMetadata,
    type WidgetDimensions,
    type WidgetFrontendFilters,
    type WidgetMetrics,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {ReportPageTemplate, WidgetTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportTemplate";

const widgetGridNumberZodValidator = zod.number().int().nonnegative();

const widgetBaseZodValidator = zod.object({
    col: widgetGridNumberZodValidator,
    colSpan: widgetGridNumberZodValidator,
    id: uuidZodValidator,
    row: widgetGridNumberZodValidator,
    rowSpan: widgetGridNumberZodValidator,
});

const widgetDateConfigZodValidator = zod.object({
    dateRange: zod.enum(DateRangeOptions).nullable(),
    dateRangeCanvasControl: uuidZodValidator.nullable(),
    endDateIso: iso8601DateTimeZodValidator.nullable(),
    startDateIso: iso8601DateTimeZodValidator.nullable(),
});

const widgetSelectionLimitsZodValidator = zod.object({
    maxDimensionSelections: widgetGridNumberZodValidator,
    maxMetricSelections: widgetGridNumberZodValidator,
    minDimensionSelections: widgetGridNumberZodValidator,
    minMetricSelections: widgetGridNumberZodValidator,
});

const widgetMetricOnlySelectionLimitsZodValidator = zod.object({
    maxMetricSelections: widgetGridNumberZodValidator,
    minMetricSelections: widgetGridNumberZodValidator,
});

const widgetFrontendFiltersZodValidator: zod.ZodType<WidgetFrontendFilters> = zod.record(
    zod.string(),
    zod.object({
        attributeId: uuidZodValidator,
        [FilterOperations.equals]: zod.array(zod.string()),
    }),
);

const widgetAttributeMetadataZodValidator: zod.ZodType<WidgetAttributeMetadata> = zod.object({
    displayName: zod.string(),
    id: uuidZodValidator,
    type: zod.enum(EratoDataTypes),
});

const widgetMetricsZodValidator: zod.ZodType<WidgetMetrics> = zod.record(
    uuidZodValidator,
    widgetAttributeMetadataZodValidator,
);

const widgetDimensionsZodValidator: zod.ZodType<WidgetDimensions> = zod.record(
    uuidZodValidator,
    widgetAttributeMetadataZodValidator,
);

const atomicFilterValueSourceZodValidator = zod.discriminatedUnion("valueType", [
    zod.object({
        globalFilterId: uuidZodValidator,
        valueType: zod.literal(BackendFilterValueTypes.globalFilter),
    }),
    zod.object({
        valueType: zod.literal(BackendFilterValueTypes.dataDriven),
    }),
]);

const atomicFilterOperationZodValidator = zod.union([
    zod.object({
        operation: zod.union([
            zod.literal(BackendFilterOperations.equals),
            zod.literal(BackendFilterOperations.contains),
            zod.null(),
        ]),
        tempValue: zod.union([zod.string(), zod.number(), zod.null()]),
        value: zod.union([zod.string(), zod.number(), zod.null()]),
    }),
    zod.object({
        operation: zod.union([zod.literal(BackendFilterOperations.in), zod.null()]),
        tempValue: zod.string().nullable(),
        value: zod.array(zod.string()).nullable(),
    }),
    zod.object({
        operation: zod.union([
            zod.literal(BackendFilterOperations.greaterThan),
            zod.literal(BackendFilterOperations.greaterThanEqualTo),
            zod.literal(BackendFilterOperations.lessThan),
            zod.literal(BackendFilterOperations.lessThanEqualTo),
            zod.null(),
        ]),
        tempValue: zod.number().nullable(),
        value: zod.number().nullable(),
    }),
    zod.object({
        operation: zod.literal(BackendFilterOperations.between),
        tempValue: zod.tuple([zod.number().nullable(), zod.number().nullable()]),
        value: zod.tuple([zod.number().nullable(), zod.number().nullable()]),
    }),
]);

const atomicFilterZodValidator: zod.ZodType<AtomicFilter> = zod
    .object({
        attribute: uuidZodValidator.nullable(),
        filterType: zod.literal(BackendFilterTypes.atomic),
        id: uuidZodValidator,
    })
    .and(atomicFilterValueSourceZodValidator)
    .and(atomicFilterOperationZodValidator);

const backendFilterZodValidator: zod.ZodType<BackendFilter> = zod.object({
    filterType: zod.literal(BackendFilterTypes.and),
    filters: zod.array(atomicFilterZodValidator),
    id: uuidZodValidator,
});

const granularityForWidgetZodValidator: zod.ZodType<GranularityForWidget> = zod.discriminatedUnion("type", [
    zod.object({
        type: zod.literal(WidgetValueType.intrinsic),
        value: zod.enum(Granularity),
    }),
    zod.object({
        type: zod.literal(WidgetValueType.canvasControl),
        value: uuidZodValidator,
    }),
]);

const chartSeriesDetailsZodValidator: zod.ZodType<ChartSeriesDetails> = zod.object({
    selectedMetric: uuidZodValidator,
    yAxisId: zod.enum(["left", "right"]),
});

const comboChartSeriesDetailsZodValidator: zod.ZodType<ComboChartSeriesDetails> = zod.object({
    selectedMetrics: zod.array(uuidZodValidator),
    type: zod.enum(ComposedChartType).nullable(),
    yAxisId: zod.enum(["left", "right"]),
});

const sharedChartWidgetConfigZodValidator = widgetBaseZodValidator
    .merge(widgetDateConfigZodValidator)
    .merge(widgetSelectionLimitsZodValidator)
    .extend({
        areCanvasControlsLinked: zod.boolean(),
        backendFilter: backendFilterZodValidator.nullable(),
        chartSeriesDetails: zod.array(chartSeriesDetailsZodValidator),
        datasetId: uuidZodValidator.nullable(),
        dimensions: widgetDimensionsZodValidator.nullable(),
        filters: widgetFrontendFiltersZodValidator,
        granularity: granularityForWidgetZodValidator,
        isTitleEdited: zod.boolean().default(false),
        metrics: widgetMetricsZodValidator.nullable(),
        selectedDimensions: zod.array(uuidZodValidator),
        selectedMetrics: zod.array(uuidZodValidator).default([]),
        title: zod.string().nullable(),
    });

const multiBarGraphWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.multiBarGraph),
});

const horizontalBarGraphWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.horizontalBarGraph),
});

const lineGraphWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.lineGraph),
});

const areaGraphWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.areaGraph),
});

const stackedBarGraphWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    selectedSlicer: uuidZodValidator.nullable(),
    type: zod.literal(WidgetType.stackedBarGraph),
});

const comboChartWidgetZodValidator = sharedChartWidgetConfigZodValidator.extend({
    chartSeriesDetails: zod.array(comboChartSeriesDetailsZodValidator),
    type: zod.literal(WidgetType.comboChart),
});

const pieOrDonutChartWidgetConfigZodValidator = widgetBaseZodValidator
    .merge(widgetDateConfigZodValidator)
    .merge(widgetSelectionLimitsZodValidator)
    .extend({
        areCanvasControlsLinked: zod.boolean(),
        backendFilter: backendFilterZodValidator.nullable(),
        datasetId: uuidZodValidator.nullable(),
        dimensions: widgetDimensionsZodValidator.nullable(),
        filters: widgetFrontendFiltersZodValidator,
        isTitleEdited: zod.boolean().default(false),
        metrics: widgetMetricsZodValidator.nullable(),
        selectedDimensions: zod.array(uuidZodValidator),
        selectedMetrics: zod.array(uuidZodValidator),
        title: zod.string().nullable(),
        valuesPrefix: zod.string().nullable(),
    });

const pieChartWidgetZodValidator = pieOrDonutChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.pieChart),
});

const donutChartWidgetZodValidator = pieOrDonutChartWidgetConfigZodValidator.extend({
    type: zod.literal(WidgetType.donutChart),
});

const tableColumnPropertiesZodValidator = zod.object({
    columnDataType: zod.enum(TableColumnDataTypes),
    columnTextOverflow: zod.enum(TableColumnTextOverflow),
    columnTitle: zod.string(),
    columnWidth: zod.number(),
    isColumnWidthEdited: zod.boolean(),
});

const tableComparisonConfigZodValidator = zod.object({
    enabled: zod.boolean().default(false),
    startDateIso: iso8601DateTimeZodValidator.nullable().default(null),
    endDateIso: iso8601DateTimeZodValidator.nullable().default(null),
});

const tableWidgetZodValidator = widgetBaseZodValidator.merge(widgetDateConfigZodValidator).extend({
    areCanvasControlsLinked: zod.boolean(),
    backendFilter: backendFilterZodValidator.nullable(),
    columnProperties: zod.record(zod.string(), tableColumnPropertiesZodValidator),
    comparison: tableComparisonConfigZodValidator.default({
        enabled: false,
        startDateIso: null,
        endDateIso: null,
    }),
    columnsToSort: zod.record(zod.string(), zod.enum(ColumnSortType).nullable()).nullable().optional(),
    datasetId: uuidZodValidator.nullable(),
    dimensions: widgetDimensionsZodValidator.nullable(),
    filters: widgetFrontendFiltersZodValidator,
    granularity: granularityForWidgetZodValidator,
    isTitleEdited: zod.boolean().default(false),
    maxDimensionSelections: zod.null(),
    maxMetricSelections: zod.null(),
    metrics: widgetMetricsZodValidator.nullable(),
    minDimensionSelections: widgetGridNumberZodValidator,
    minMetricSelections: widgetGridNumberZodValidator,
    selectedDimensions: zod.array(uuidZodValidator),
    selectedMetrics: zod.array(uuidZodValidator),
    transposeDimensionId: uuidZodValidator.nullable().optional(),
    title: zod.string().nullable(),
    type: zod.literal(WidgetType.table),
});

const numberTileWidgetZodValidator = widgetBaseZodValidator
    .merge(widgetDateConfigZodValidator)
    .merge(widgetMetricOnlySelectionLimitsZodValidator)
    .extend({
        areCanvasControlsLinked: zod.boolean(),
        backendFilter: backendFilterZodValidator.nullable(),
        datasetId: uuidZodValidator.nullable(),
        decimalPlaces: widgetGridNumberZodValidator,
        filters: widgetFrontendFiltersZodValidator,
        fontSizeMultiplier: zod.number(),
        isTitleEdited: zod.boolean().default(false),
        metrics: widgetMetricsZodValidator.nullable(),
        numberSystem: zod.enum(NumberSystems),
        selectedMetrics: zod.array(uuidZodValidator),
        spreadValue: zod.boolean().optional(),
        title: zod.string().nullable(),
        type: zod.literal(WidgetType.numberTile),
        valuePrefix: zod.string().nullable(),
        valueSuffix: zod.string().nullable(),
    });

const progressBarWidgetZodValidator = widgetBaseZodValidator
    .merge(widgetDateConfigZodValidator)
    .merge(widgetMetricOnlySelectionLimitsZodValidator)
    .extend({
        backendFilter: backendFilterZodValidator.nullable(),
        datasetId: uuidZodValidator.nullable(),
        metrics: widgetMetricsZodValidator.nullable(),
        selectedMetrics: zod.array(uuidZodValidator),
        targetValue: zod.number().nullable(),
        title: zod.string().nullable(),
        type: zod.literal(WidgetType.progressBar),
    });

const gaugeWidgetZodValidator = progressBarWidgetZodValidator.extend({
    type: zod.literal(WidgetType.gauge),
});

const textWidgetZodValidator = widgetBaseZodValidator.extend({
    bold: zod.boolean(),
    fontSizeMultiplier: zod.number(),
    italic: zod.boolean(),
    strikethrough: zod.boolean(),
    text: zod.string(),
    textHorizontalAlign: zod.enum(["left", "center", "right"]),
    textVerticalAlign: zod.enum(["top", "center", "bottom"]),
    type: zod.literal(WidgetType.text),
    underline: zod.boolean(),
});

const imageWidgetZodValidator = widgetBaseZodValidator.extend({
    image: zod.string().nullable(),
    type: zod.literal(WidgetType.image),
});

export const storedWidgetZodValidator: zod.ZodType<StoredWidget> = zod.discriminatedUnion("type", [
    multiBarGraphWidgetZodValidator,
    stackedBarGraphWidgetZodValidator,
    horizontalBarGraphWidgetZodValidator,
    lineGraphWidgetZodValidator,
    areaGraphWidgetZodValidator,
    comboChartWidgetZodValidator,
    tableWidgetZodValidator,
    numberTileWidgetZodValidator,
    pieChartWidgetZodValidator,
    donutChartWidgetZodValidator,
    textWidgetZodValidator,
    progressBarWidgetZodValidator,
    gaugeWidgetZodValidator,
    imageWidgetZodValidator,
]);

const reportTemplateWidgetFieldsZodValidator = {
    datasetTemplateId: uuidZodValidator.nullable(),
};

export const reportTemplateWidgetZodValidator: zod.ZodType<WidgetTemplate> = zod.discriminatedUnion("type", [
    multiBarGraphWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    stackedBarGraphWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    horizontalBarGraphWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    lineGraphWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    areaGraphWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    comboChartWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    tableWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    numberTileWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    pieChartWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    donutChartWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    textWidgetZodValidator,
    progressBarWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    gaugeWidgetZodValidator.extend(reportTemplateWidgetFieldsZodValidator),
    imageWidgetZodValidator,
]);

export const reportPageZodValidator: zod.ZodType<ReportPage> = zod.object({
    id: uuidZodValidator,
    title: zod.string(),
    widgets: zod.array(storedWidgetZodValidator),
});

export const reportTemplatePageZodValidator: zod.ZodType<ReportPageTemplate> = zod.object({
    id: uuidZodValidator,
    title: zod.string(),
    widgets: zod.array(reportTemplateWidgetZodValidator),
});

export const reportPagesZodValidator: zod.ZodType<Array<ReportPage>> = zod.array(reportPageZodValidator);

export const reportTemplatePagesZodValidator: zod.ZodType<Array<ReportPageTemplate>> = zod.array(reportTemplatePageZodValidator);

const dabbaToAttributeMappingZodValidator = zod.record(
    uuidZodValidator,
    zod.object({
        dimensions: zod.array(uuidZodValidator),
        metrics: zod.array(uuidZodValidator),
    }),
);

const canvasControlCommonFieldsZodValidator = zod.object({
    dabbaToAttributeMapping: dabbaToAttributeMappingZodValidator,
    id: uuidZodValidator,
    isActive: zod.boolean(),
    name: zod.string(),
});

const canvasControlGranularityZodValidator = canvasControlCommonFieldsZodValidator.extend({
    defaultValue: zod.string(),
    type: zod.literal(CanvasControlType.granularity),
    value: zod.string(),
    values: zod.array(zod.string()),
});

const canvasControlDateRangeZodValidator = canvasControlCommonFieldsZodValidator.extend({
    dateRange: zod.enum(DateRangeOptions).nullable(),
    defaultDateRange: zod.enum(DateRangeOptions).nullable(),
    defaultValue: zod.tuple([iso8601DateTimeZodValidator.nullable(), iso8601DateTimeZodValidator.nullable()]),
    type: zod.literal(CanvasControlType.dateRange),
    value: zod.tuple([iso8601DateTimeZodValidator.nullable(), iso8601DateTimeZodValidator.nullable()]),
});

const canvasControlSelectZodValidator = canvasControlCommonFieldsZodValidator.extend({
    defaultValue: zod.string(),
    type: zod.literal(CanvasControlType.select),
    value: zod.string(),
});

const canvasControlMultiSelectZodValidator = canvasControlCommonFieldsZodValidator.extend({
    defaultValue: zod.array(zod.string()),
    type: zod.literal(CanvasControlType.multiSelect),
    value: zod.array(zod.string()),
});

export const canvasControlZodValidator: zod.ZodType<CanvasControl> = zod.discriminatedUnion("type", [
    canvasControlGranularityZodValidator,
    canvasControlDateRangeZodValidator,
    canvasControlSelectZodValidator,
    canvasControlMultiSelectZodValidator,
]);

export const canvasControlsZodValidator: zod.ZodType<CanvasControls> = zod.record(
    uuidZodValidator,
    canvasControlZodValidator,
);
